import type { UsersReportRow } from '@/services';
import {
  formatDateTimeToBrazil,
  formatDateToBrazil,
} from '@/shared/utils/format-date.util';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { getScopeFromGroupName } from '@/shared/utils/get-group';
import { getRoleLabel } from '@/shared/utils/get-role-label';
import { getUserStatus } from '@/shared/utils/get-user-status';
import type { DataTableColumn } from '@noorden-insurtech/alba-design-system';

/** Coluna da tabela ↔ campo de ordenação aceito pela API. */
export const USERS_REPORT_SORT_FIELD_BY_COLUMN: Record<string, string> = {
  name: 'name',
  status: 'status',
  party: 'brokerName',
  invitedAt: 'invitedAt',
  lastLoginAt: 'lastLoginAt',
};

const STATUS_DOT_CLASS: Record<UsersReportRow['status'], string> = {
  FORCE_CHANGE_PASSWORD: 'bg-alba-warning-yellow-500',
  ACTIVE: 'bg-alba-success-green-500',
  INACTIVE: 'bg-alba-gray-400',
};

/**
 * Classes para o contêiner da tabela: layout fixo (as colunas respeitam o
 * `size` e o conteúdo trunca) e padding horizontal menor que o padrão do
 * design system.
 */
export const USERS_REPORT_TABLE_CLASS =
  '[&_table]:table-fixed [&_td]:px-3 [&_th]:px-3';

/** Quantos produtos listar por linha antes de resumir em "+N". */
const MAX_VISIBLE_PRODUCTS = 2;

const EMPTY = <span className='text-xs text-alba-gray-500'>-</span>;

/** Duas linhas compactas com truncamento (o texto completo fica no `title`). */
function TwoLine({
  top,
  bottom,
}: {
  top?: string | null;
  bottom?: string | null;
}) {
  return (
    <div className='flex min-w-0 flex-col'>
      <span
        className='truncate text-xs font-semibold text-alba-black-500'
        title={top || undefined}
      >
        {top || '-'}
      </span>
      {bottom ? (
        <span
          className='truncate text-[11px] text-alba-gray-600'
          title={bottom}
        >
          {bottom}
        </span>
      ) : null}
    </div>
  );
}

// A tabela usa layout fixo (ver `USERS_REPORT_TABLE_CLASS`): estas larguras
// somam 950px, o mínimo da área de conteúdo de Configurações, e crescem
// proporcionalmente em telas maiores. O conteúdo trunca em vez de alargar a
// coluna, então não há scroll horizontal.
export const usersReportColumns: DataTableColumn<UsersReportRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Nome',
    size: 210,
    sortable: true,
    cell: ({ row }) => (
      <div className='flex min-w-0 flex-col'>
        <span
          className='truncate text-xs font-semibold text-alba-black-500'
          title={row.original.name}
        >
          {row.original.name}
        </span>
        <span
          className='truncate text-[11px] text-alba-gray-600'
          title={row.original.email}
        >
          {row.original.email}
        </span>
        {row.original.registerNumber && (
          <span className='text-[11px] text-alba-gray-600'>
            CPF {formatRegisterNumber(row.original.registerNumber)}
          </span>
        )}
      </div>
    ),
  },
  {
    id: 'profile',
    accessorKey: 'groupName',
    header: 'Perfil',
    size: 100,
    sortable: false,
    cell: ({ row }) => (
      <TwoLine
        top={getRoleLabel(row.original.groupName)}
        bottom={getScopeFromGroupName(row.original.groupName)}
      />
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 125,
    sortable: true,
    cell: ({ row }) => (
      <div className='flex items-center gap-1.5'>
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT_CLASS[row.original.status]}`}
        />
        <span className='text-xs text-alba-black-500'>
          {getUserStatus(row.original.status)}
        </span>
      </div>
    ),
  },
  {
    id: 'party',
    accessorKey: 'brokerName',
    header: 'Corretora / Assessoria',
    size: 170,
    sortable: true,
    cell: ({ row }) =>
      row.original.brokerName || row.original.advisoryName ? (
        <TwoLine
          top={row.original.brokerName || row.original.advisoryName}
          bottom={row.original.brokerName ? row.original.advisoryName : null}
        />
      ) : (
        EMPTY
      ),
  },
  {
    id: 'products',
    accessorKey: 'products',
    header: 'Produtos',
    size: 125,
    sortable: false,
    cell: ({ row }) => {
      const { products } = row.original;
      if (!products.length) return EMPTY;
      const hidden = products.length - MAX_VISIBLE_PRODUCTS;
      return (
        <div
          className='flex min-w-0 flex-col'
          title={products.map(product => product.name).join(', ')}
        >
          {products.slice(0, MAX_VISIBLE_PRODUCTS).map(product => (
            <span
              key={product.code}
              className='truncate text-xs text-alba-black-500'
            >
              {product.name}
            </span>
          ))}
          {hidden > 0 && (
            <span className='text-[11px] text-alba-gray-600'>
              +{hidden} {hidden === 1 ? 'produto' : 'produtos'}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'invitedAt',
    accessorKey: 'invitedAt',
    header: 'Convite',
    size: 125,
    sortable: true,
    cell: ({ row }) => {
      const { invitedAt, inviteExpiresAt, inviteExpired } = row.original;
      if (!invitedAt) return EMPTY;
      return (
        <div className='flex min-w-0 flex-col'>
          <span
            className='text-xs text-alba-black-500'
            title='Data de envio do convite'
          >
            {formatDateToBrazil(invitedAt)}
          </span>
          <span
            className={`text-[11px] ${inviteExpired ? 'font-bold text-alba-error-red-500' : 'text-alba-gray-600'}`}
          >
            {inviteExpired ? 'Expirou' : 'Expira'}{' '}
            {formatDateToBrazil(inviteExpiresAt)}
          </span>
        </div>
      );
    },
  },
  {
    id: 'lastLoginAt',
    accessorKey: 'lastLoginAt',
    header: 'Último login',
    size: 95,
    sortable: true,
    cell: ({ row }) =>
      row.original.lastLoginAt ? (
        <span className='text-xs text-alba-black-500'>
          {formatDateTimeToBrazil(row.original.lastLoginAt)}
        </span>
      ) : (
        EMPTY
      ),
  },
];
